import { createReducer, requestStatus } from '../../../../lib/Utils'
import {
  SEARCH_CAMPAIGNS_BY_RETAILER_ACCOUNT,
  SEARCH_CAMPAIGNS_BY_RETAILER_ACCOUNT_FULFILLED,
  SEARCH_CAMPAIGNS_BY_RETAILER_ACCOUNT_REJECTED,
} from './actions'

/**
 * @typedef RetailerAccountsCampaignsSearchStore
 * @property {object} byRetailerAccountId
 * @property {CampaignsSearchRetailerAccount} byRetailerAccountId[{number}]
 */

/**
 * @typedef CampaignsSearchRetailerAccount
 * @property {object[][]} searchCampaigns
 * @property {object} searchCampaignsFilters
 * @property {{status: requestStatus, error: object|array|null, offset: number}[]} searchCampaignsRequests
 * @property {number} [searchCampaignsTotal] - total number of results for the current search
 */

/**
 * @type RetailerAccountsCampaignsSearchStore
 */
const initialState = {
  byRetailerAccountId: {},
}

export const checkIfCampaignFiltersChanged = (previousFilters, newFilters) => {
  const {
    // We don't actually do anything with these, we just want an object that
    // omits them.
    /* eslint-disable no-unused-vars */
    offset: previousFiltersOffset,
    /* eslint-enable */
    ...previousFiltersNormalized
  } = previousFilters
  const {
    // We don't actually do anything with these, we just want an object that
    // omits them.
    /* eslint-disable no-unused-vars */
    offset: newFiltersOffset,
    /* eslint-enable */
    ...newFiltersNormalized
  } = newFilters

  return (
    JSON.stringify(previousFiltersNormalized) !==
    JSON.stringify(newFiltersNormalized)
  )
}

export default createReducer(initialState, {
  [SEARCH_CAMPAIGNS_BY_RETAILER_ACCOUNT]: (state, action) => {
    if (!action.payload.retailerAccountId) {
      return state
    }
    const prevRetailer =
      (state.byRetailerAccountId &&
        state.byRetailerAccountId[action.payload.retailerAccountId]) ||
      {}
    let {
      searchCampaigns = [],
      searchCampaignsRequests = [],
      searchCampaignsFilters,
      searchCampaignsTotal,
    } = prevRetailer
    const { offset = 0, ...filters } = action.payload
    // Check if the filters match the previous filters
    if (searchCampaignsFilters) {
      const filtersChanged = checkIfCampaignFiltersChanged(
        searchCampaignsFilters,
        filters,
      )
      if (filtersChanged) {
        // Clear data and requests
        searchCampaignsRequests = []
        searchCampaigns = []
        searchCampaignsFilters = filters
        searchCampaignsTotal = null
      }
    } else {
      searchCampaignsFilters = filters
    }
    let page = action.payload.perPage ? offset / action.payload.perPage : 0
    searchCampaignsRequests = searchCampaignsRequests.slice(0)
    searchCampaignsRequests[page] = {
      status: requestStatus.pending,
      offset,
      error: null,
    }
    const retailer = {
      ...prevRetailer,
      searchCampaigns,
      searchCampaignsFilters,
      searchCampaignsRequests,
      searchCampaignsTotal,
    }
    return {
      ...state,
      byRetailerAccountId: {
        ...state.byRetailerAccountId,
        [action.payload.retailerAccountId]: retailer,
      },
    }
  },
  [SEARCH_CAMPAIGNS_BY_RETAILER_ACCOUNT_FULFILLED]: (state, action) => {
    const { retailerAccountId, offset = 0 } = action.payload.filters
    const previousRetailer =
      (state.byRetailerAccountId &&
        state.byRetailerAccountId[retailerAccountId]) ||
      {}
    const page = action.payload.filters.perPage
      ? offset / action.payload.filters.perPage
      : 0
    let {
      searchCampaigns = [],
      searchCampaignsRequests = [],
    } = previousRetailer
    // Save page data
    searchCampaigns = searchCampaigns.slice(0)
    searchCampaigns[page] = action.payload.response
    // Update request status
    searchCampaignsRequests = searchCampaignsRequests.slice(0)
    searchCampaignsRequests[page] = {
      status: requestStatus.complete,
      error: null,
      offset,
      lastUpdate: action.payload.requestDate,
    }
    // Update total
    const searchCampaignsTotal = action.payload.total
    const retailer = {
      ...previousRetailer,
      searchCampaignsRequests,
      searchCampaignsTotal,
      searchCampaigns,
    }
    return {
      ...state,
      byRetailerAccountId: {
        ...state.byRetailerAccountId,
        [retailerAccountId]: retailer,
      },
    }
  },
  [SEARCH_CAMPAIGNS_BY_RETAILER_ACCOUNT_REJECTED]: (state, action) => {
    if (action.payload instanceof Error) {
      return state
    }
    const { retailerAccountId, offset = 0 } = action.payload.filters
    const previousRetailer =
      state.byRetailerAccountId && state.byRetailerAccountId[retailerAccountId]
    let { searchCampaignsRequests = [] } = previousRetailer
    const page = action.payload.filters.perPage
      ? offset / action.payload.filters.perPage
      : 0
    // Update request status
    searchCampaignsRequests = searchCampaignsRequests.slice(0)
    searchCampaignsRequests[page] = {
      status: requestStatus.rejected,
      error: action.payload.response,
      offset,
      lastUpdate: action.payload.requestDate,
    }
    const retailer = {
      ...previousRetailer,
      searchCampaignsRequests,
    }
    return {
      ...state,
      byRetailerAccountId: {
        ...state.byRetailerAccountId,
        [retailerAccountId]: retailer,
      },
    }
  },
})
